// src/data/resourceDocuments.ts
// -----------------------------
// Repository for `resource_documents` (the Resources document store; db-138).
// Forms/templates uploaded under Manage → Resources Management, then LINKED from
// a Resources page via the stable portal link `/resource-doc/<id>`.
//
// The row exists to decouple three things that must vary independently:
//   • `name`      — the display label (renameable, free)
//   • `id`        — the LINK; permanent, this is what gets pasted into a page
//   • `file_path` — the stored object; changes every time the file is Replaced
// which is why replacing a form's file leaves existing page links working.
//
// RLS (db-138): internal staff read; Supervisors + SuperUsers write. Soft-delete
// via `deactivated` — no hard delete, so an already-pasted link degrades to a
// friendly "no longer available" rather than a raw 404.

import type { Database } from "../types/database.generated";
import { logger } from "../utils/logger";
import { getTypedClient } from "../utils/supabase";

export type ResourceDocumentRow = Database["public"]["Tables"]["resource_documents"]["Row"];
export type ResourceDocumentInsert = Database["public"]["Tables"]["resource_documents"]["Insert"];
export type ResourceDocumentUpdate = Database["public"]["Tables"]["resource_documents"]["Update"];

const COLS =
  "id, name, file_path, mime_type, size_bytes, uploaded_by, deactivated, created_at, updated_at";

// Active documents, newest first (matches idx_resource_documents_active).
// Null on error / no client, per the repo convention.
export async function listResourceDocuments(): Promise<ResourceDocumentRow[] | null> {
  const c = getTypedClient();
  if (!c) return null;
  const { data, error } = await c
    .from("resource_documents")
    .select(COLS)
    .eq("deactivated", false)
    .order("created_at", { ascending: false });
  if (error) {
    logger.warn("listResourceDocuments failed:", error.message);
    return null;
  }
  return (data ?? []) as unknown as ResourceDocumentRow[];
}

// A single document BY ID — deliberately does NOT filter on `deactivated`, so the
// /resource-doc/:id route can tell "removed" (show a friendly notice) apart from
// "never existed" (a bad link). Null when absent.
export async function getResourceDocumentById(id: string): Promise<ResourceDocumentRow | null> {
  const c = getTypedClient();
  if (!c) return null;
  const { data, error } = await c
    .from("resource_documents")
    .select(COLS)
    .eq("id", id)
    .maybeSingle();
  if (error) {
    logger.warn("getResourceDocumentById failed:", error.message);
    return null;
  }
  return (data as unknown as ResourceDocumentRow) ?? null;
}

// Insert a document row. The caller generates the `id` up front (crypto.randomUUID)
// because the storage path is keyed on it — see uploadResourceDoc in storage.ts.
export async function createResourceDocument(
  row: ResourceDocumentInsert
): Promise<{ data: ResourceDocumentRow | null; error: { message: string } | null }> {
  const c = getTypedClient();
  if (!c) return { data: null, error: { message: "No database connection." } };
  const { data, error } = await c.from("resource_documents").insert(row).select(COLS).single();
  if (error) logger.warn("createResourceDocument failed:", error.message);
  return { data: (data as unknown as ResourceDocumentRow) ?? null, error };
}

// Rename (`name`) or record a replaced file (`file_path` / `mime_type` /
// `size_bytes`). Never touches `id`, so the link survives both.
export async function updateResourceDocument(
  id: string,
  patch: ResourceDocumentUpdate
): Promise<{ error: { message: string } | null }> {
  const c = getTypedClient();
  if (!c) return { error: { message: "No database connection." } };
  const { error } = await c
    .from("resource_documents")
    .update({ ...patch, updated_at: new Date().toISOString() })
    .eq("id", id);
  if (error) logger.warn("updateResourceDocument failed:", error.message);
  return { error };
}

// Soft delete (schema convention). The stored object is intentionally LEFT in
// place — a deactivation can be undone, and orphaning the file would make that
// irreversible.
export async function deactivateResourceDocument(
  id: string
): Promise<{ error: { message: string } | null }> {
  return updateResourceDocument(id, { deactivated: true });
}
